// Feb 2026 — FL-Dashboard-Widget für offene SB-Anfragen
// Liest aggregierte Anfragen vom Backend, gruppiert nach Compliance-Issue-Code.
// Klick auf einen Beleg → navigate zum Korrektur-Drawer in Finances/Invoices.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Inbox, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';

export default function SBRequestsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCode, setExpandedCode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get('/inbox/sb-requests');
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ total: 0, groups: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Sanftes Polling alle 90s (keine Aggressive-Refetch-Race)
    const iv = setInterval(load, 90000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Kein Widget wenn keine Anfragen oder noch lädt
  if (loading || !data || data.total === 0) return null;

  const openItem = (item) => {
    if (item.entity_type === 'expense' && item.entity_id) {
      navigate(`/finances?openExpense=${item.entity_id}`);
    } else if (item.entity_type === 'invoice' && item.entity_id) {
      navigate(`/invoices?openInvoice=${item.entity_id}`);
    }
  };

  const criticalCount = data.groups
    .filter(g => g.severity === 'critical')
    .reduce((s, g) => s + g.count, 0);

  return (
    <section
      className="rounded-2xl border-2 border-amber-200 bg-amber-50/60 p-4 shadow-sm"
      data-testid="sb-requests-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Inbox className="h-5 w-5 text-amber-700" />
            {criticalCount > 0 && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-amber-50" />
            )}
          </div>
          <div>
            <h2 className="text-sm font-bold text-amber-900" data-testid="sb-requests-title">
              {data.total} offene Anfrage{data.total !== 1 ? 'n' : ''} von deinem Steuerberater
            </h2>
            <p className="text-[11px] text-amber-800/80">
              {criticalCount > 0
                ? `${criticalCount} kritisch — bitte zeitnah bearbeiten`
                : 'Bitte prüfen und beantworten'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        {data.groups.map((g) => {
          const expanded = expandedCode === g.issue_code;
          const palette = g.severity === 'critical'
            ? 'border-rose-300 bg-rose-50 text-rose-900'
            : g.severity === 'warning'
              ? 'border-amber-300 bg-white text-amber-900'
              : 'border-slate-200 bg-white text-slate-700';
          return (
            <div
              key={g.issue_code}
              className={`rounded-lg border-2 ${palette}`}
              data-testid={`sb-request-group-${g.issue_code}`}
            >
              <button
                type="button"
                onClick={() => setExpandedCode(expanded ? null : g.issue_code)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {g.severity === 'critical' && <AlertCircle className="h-4 w-4 shrink-0" />}
                  <span className="text-sm font-semibold">{g.title}</span>
                  <span className="text-xs opacity-70">· {g.count}</span>
                </div>
                {expanded ? <ChevronDown className="h-4 w-4 opacity-70" /> : <ChevronRight className="h-4 w-4 opacity-70" />}
              </button>
              {expanded && (
                <div className="px-3 pb-3 pt-0 space-y-1.5">
                  {g.items.map((item) => (
                    <button
                      key={item.comment_id}
                      type="button"
                      onClick={() => openItem(item)}
                      className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white/80 hover:bg-white border border-current/15 rounded-md text-left transition"
                      data-testid={`sb-request-item-${item.comment_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">
                          {item.vendor || item.description || 'Eintrag'}
                          {item.date && <span className="opacity-60 ml-2">{item.date}</span>}
                        </div>
                        <div className="text-[11px] opacity-75 truncate mt-0.5 italic">
                          „{item.text}"
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {item.amount != null && (
                          <span className="text-xs font-semibold tabular-nums">
                            {Number(item.amount).toFixed(2)} €
                          </span>
                        )}
                        <span className="text-[10px] opacity-50">↗</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
